import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import { IconArrowsRepeatRightLeft } from "central-icons/IconArrowsRepeatRightLeft";
import { IconArrowTriangleTop } from "central-icons/IconArrowTriangleTop";
import { IconBookmark } from "central-icons/IconBookmark";
import { IconCirclePlus } from "central-icons/IconCirclePlus";
import { IconCodeInsert } from "central-icons/IconCodeInsert";
import { IconColors } from "central-icons/IconColors";
import { IconCup } from "central-icons/IconCup";
import { IconEmail1 } from "central-icons/IconEmail1";
import { IconHomeOpen } from "central-icons/IconHomeOpen";
import { IconPaintBucket } from "central-icons/IconPaintBucket";
import { IconPrompt } from "central-icons/IconPrompt";
import { IconUser } from "central-icons/IconUser";
import { IconX } from "central-icons/IconX";

type IconComponent = React.ComponentType<CentralIconBaseProps>;

interface RouteInfo {
  blurb?: string;
  description: string;
  icon: IconComponent;
  indexed: boolean;
  inNav: boolean;
  label: string;
  navLabel?: string;
}

// biome-ignore assist/source/useSortedKeys: Insertion order defines the navigation or dropdown order.
const ROUTES = {
  "/": {
    description:
      "Build palettes with consistent lightness and predictable shades, convert between formats, and explore gamut — all in OKLCH.",
    icon: IconHomeOpen,
    indexed: true,
    inNav: true,
    label: "Pick, convert and generate OKLCH colors",
    navLabel: "Home",
  },
  "/skill": {
    description:
      "An agent skill for OKLCH — convert a project's colors, generate palettes, check contrast, and handle gamut clamping.",
    icon: IconPrompt,
    indexed: true,
    inNav: true,
    label: "OKLCH Skill",
    navLabel: "Skill",
  },
  "/create": {
    blurb: "Create a color palette based on a hex color or OKLCH controls.",
    description:
      "Build OKLCH palettes that keep their lightness and chroma across shades. Export to CSS or Tailwind.",
    icon: IconCirclePlus,
    indexed: true,
    inNav: true,
    label: "Create",
  },
  "/convert": {
    blurb: "Convert from hex to OKLCH and vice versa.",
    description:
      "Convert HEX, RGB, and HSL to OKLCH and back. Copy the result as CSS or a Tailwind value.",
    icon: IconArrowsRepeatRightLeft,
    indexed: true,
    inNav: true,
    label: "Convert",
  },
  "/color-palettes": {
    blurb: "A collection of color palettes created using OKLCH colors.",
    description:
      "A collection of OKLCH color palettes, including the full Radix scales. Click any color to copy it.",
    icon: IconPaintBucket,
    indexed: true,
    inNav: true,
    label: "Color Palettes",
  },
  "/gradients": {
    blurb:
      "Compare OKLCH vs sRGB gradient interpolation and copy the CSS for either.",
    description:
      "Build OKLCH gradients that interpolate cleanly, without the muddy midpoints you get in RGB. Copy the CSS.",
    icon: IconColors,
    indexed: true,
    inNav: true,
    label: "Gradients",
  },
  "/gamut": {
    blurb:
      "Explore the OKLCH color space and see gamut boundaries for sRGB and Display P3.",
    description:
      "Compare sRGB and Display-P3 in 3D and see which colors each one can actually show.",
    icon: IconArrowTriangleTop,
    indexed: true,
    inNav: true,
    label: "Gamut Visualizer",
  },
  "/bulk-convert": {
    blurb:
      "Convert multiple colors to or from OKLCH. You can paste in your entire CSS config.",
    description:
      "Paste a list of colors and convert all of them to OKLCH, HEX, RGB, or HSL at once.",
    icon: IconCodeInsert,
    indexed: true,
    inNav: true,
    label: "Bulk Convert",
  },
  "/saved": {
    blurb: "View and manage your saved color palettes and gradients.",
    description: "View and manage your saved color palettes and gradients.",
    icon: IconBookmark,
    indexed: false,
    inNav: true,
    label: "Saved",
  },
  "/sign-in": {
    description: "Sign in to your account to save your palettes and gradients.",
    icon: IconUser,
    indexed: false,
    inNav: false,
    label: "Sign In",
  },
} satisfies Record<string, RouteInfo>;

export type RouteHref = keyof typeof ROUTES;

export interface Route extends RouteInfo {
  href: RouteHref;
}

export const routes: Route[] = (Object.keys(ROUTES) as RouteHref[]).map(
  (href) => ({ href, ...ROUTES[href] })
);

export function routeFor(href: RouteHref): Route {
  return { href, ...ROUTES[href] };
}

export const indexedRoutes = routes.filter((route) => route.indexed);

export type ExternalHref = `https://${string}` | `mailto:${string}`;

export interface NavItem {
  href: RouteHref | ExternalHref;
  icon: IconComponent;
  label: string;
}

export const mainNavItems: NavItem[] = routes
  .filter((route) => route.inNav)
  .map((route) => ({
    href: route.href,
    icon: route.icon,
    label: route.navLabel ?? route.label,
  }));

export const secondaryNavItems: NavItem[] = [
  {
    href: "https://ko-fi.com/jakubkrehel",
    icon: IconCup,
    label: "Buy me a coffee",
  },
  {
    href: "mailto:jakub@kbo.sk",
    icon: IconEmail1,
    label: "Contact",
  },
  {
    href: "https://x.com/jakubkrehel",
    icon: IconX,
    label: "X (Twitter)",
  },
];
